import React, { useState, useEffect } from 'react';
import { subscriptionApi } from '../../api/subscriptionApi';
import { DocumentArrowDownIcon, CalendarIcon, CurrencyRupeeIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import toast from 'react-hot-toast';

const BillingHistory = () => {
  const { user, activeBusiness } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await subscriptionApi.getPaymentHistory();
      if (res.data.success) {
        setHistory(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load billing history', error);
      toast.error('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = (payment) => {
    try {
      const doc = new jsPDF();
      
      // Basic Setup
      doc.setFontSize(22);
      doc.setTextColor('#dc343b');
      doc.text('Khatabook Business Suite', 14, 20);
      
      doc.setFontSize(10);
      doc.setTextColor('#666666');
      doc.text('Your Trusted Digital Ledger', 14, 26);
      
      // Invoice Details
      doc.setFontSize(16);
      doc.setTextColor('#0f172a');
      doc.text('TAX INVOICE / RECEIPT', 14, 40);
      
      doc.setFontSize(10);
      doc.setTextColor('#333333');
      doc.text(`Receipt No: ${payment.paymentId || 'N/A'}`, 14, 50);
      doc.text(`Order ID: ${payment.orderId || 'N/A'}`, 14, 55);
      doc.text(`Date: ${format(new Date(payment.date), 'dd MMM yyyy, hh:mm a')}`, 14, 60);
      doc.text(`Status: ${payment.status.toUpperCase()}`, 14, 65);

      // Customer Details
      doc.setFontSize(12);
      doc.setTextColor('#0f172a');
      doc.text('Billed To:', 120, 50);
      
      doc.setFontSize(10);
      doc.setTextColor('#333333');
      doc.text(`Name: ${user?.name || 'N/A'}`, 120, 56);
      doc.text(`Email: ${user?.email || 'N/A'}`, 120, 61);
      doc.text(`Phone: ${user?.phone || 'N/A'}`, 120, 66);
      if (activeBusiness?.businessName) {
        doc.text(`Business: ${activeBusiness.businessName}`, 120, 71);
      }

      // Table Data
      const tableColumn = ["Description", "Billing Cycle", "Status", "Amount"];
      const planName = (payment.planSlug || 'N/A').toUpperCase();
      const tableRows = [
        [`Khatabook ${planName} Plan Subscription`, payment.billingCycle === 'yearly' ? 'Yearly' : 'Monthly', payment.status.toUpperCase(), `Rs. ${payment.amount}`]
      ];

      doc.autoTable({
        startY: 85,
        head: [tableColumn],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: '#dc343b', textColor: '#ffffff' },
        styles: { fontSize: 10, cellPadding: 5 }
      });

      // Total
      const finalY = doc.lastAutoTable.finalY || 85;
      doc.setFontSize(12);
      doc.setTextColor('#0f172a');
      doc.text(`Total Amount Paid: Rs. ${payment.amount}`, 14, finalY + 15);

      // Footer
      doc.setFontSize(10);
      doc.setTextColor('#999999');
      doc.text('Thank you for choosing Khatabook!', 14, finalY + 35);
      doc.text('This is a computer-generated document and does not require a signature.', 14, finalY + 42);

      // Save PDF
      doc.save(`Invoice_${payment.paymentId || payment.orderId || 'Download'}.pdf`);
      toast.success('Invoice downloaded successfully');
    } catch (err) {
      console.error('Invoice generation failed:', err);
      toast.error('Failed to generate invoice');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto font-sans">
      <div className="mb-8">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Billing & Invoices</h1>
        <p className="text-sm text-gray-500 mt-1">View your past subscriptions and download invoices.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-20 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dc343b]"></div>
            <span className="ml-3">Loading history...</span>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-20">
            <CurrencyRupeeIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <h3 className="text-lg font-bold text-gray-900">No Billing History</h3>
            <p className="text-sm text-gray-500">You haven't made any purchases yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {history.map((payment, idx) => (
                  <tr key={payment._id || idx} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                        {format(new Date(payment.date), 'dd MMM yyyy')}
                      </div>
                      <div className="text-[11px] text-gray-400 ml-6">{format(new Date(payment.date), 'hh:mm a')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900 capitalize">{payment.planSlug} Plan</div>
                      <div className="text-[11px] text-gray-500 capitalize">{payment.billingCycle} billing</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900 flex items-center">
                        ₹{payment.amount?.toLocaleString('en-IN') || 0}
                      </div>
                      <div className="text-[10px] text-gray-400 font-medium">Order: {payment.orderId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                        payment.status === 'success' ? 'bg-green-100 text-green-700' : 
                        payment.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {payment.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {payment.status === 'success' && (
                        <button
                          onClick={() => downloadInvoice(payment)}
                          className="inline-flex items-center justify-center p-2 rounded-lg bg-red-50 text-[#dc343b] hover:bg-red-100 hover:text-red-700 transition-colors group"
                          title="Download Invoice"
                        >
                          <DocumentArrowDownIcon className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingHistory;
